/**
 * NetRuntime - centralized runtime environment detection for networking.
 * Distinguishes between Server (Node.js headless), Client (browser connected to server),
 * and Standalone (browser running independently).
 */

/**
 * Enum representing the type of runtime environment.
 * Ordered by authority: Standalone and Server have authority, Client does not.
 * Use: `runtimeType <= NetRuntimeType.Server` to check if has authority.
 */
export enum NetRuntimeType {
  /** Standalone browser game client that is authoritative over its own state */
  Standalone = 0,
  /** Headless server running in Node.js */
  DedicatedServer = 1,
  /** Client running in a browser connected to a server */
  Client = 2
}

/**
 * Singleton class for managing runtime environment detection.
 */
class NetRuntimeManager {
  private runtimeType: NetRuntimeType | null = null;
  private initialized = false;

  /**
   * Initialize the runtime type.
   * Should be called once at application startup.
   *
   * @param type - The runtime type to set
   */
  public initialize(type: NetRuntimeType): void {
    if (this.initialized) {
      console.warn('[NetRuntime] Already initialized. Ignoring subsequent initialize() call.');
      return;
    }

    this.runtimeType = type;
    this.initialized = true;
    console.log(`[NetRuntime] Initialized as ${type}`);
  }

  /**
   * Get the current runtime type.
   * If not explicitly initialized, auto-detects based on environment.
   *
   * @returns The current runtime type
   */
  public getType(): NetRuntimeType {
    if (!this.initialized) {
      // Auto-detect: if window is undefined, assume server
      this.runtimeType = typeof window === 'undefined'
        ? NetRuntimeType.DedicatedServer
        : NetRuntimeType.Client;
      console.warn(
        `[NetRuntime] Not explicitly initialized. Auto-detected as ${this.runtimeType}. ` +
        'Consider calling NetRuntime.initialize() at startup.'
      );
      this.initialized = true;
    }

    return this.runtimeType!;
  }

  /**
   * Check if running as a standalone client.
   *
   * @returns true if running in standalone mode
   */
  public isStandalone(): boolean {
    return this.getType() === NetRuntimeType.Standalone;
  }

  /**
   * Check if running as a dedicated server.
   *
   * @returns true if running in dedicated server mode
   */
  public isDedicatedServer(): boolean {
    return this.getType() === NetRuntimeType.DedicatedServer;
  }

  /**
   * Check if running as a server (has authority).
   * Returns true for Standalone and DedicatedServer, false for Client.
   *
   * @returns true if running in server mode or standalone mode
   */
  public isServer(): boolean {
    return this.getType() < NetRuntimeType.Client;
  }

  /**
   * Check if running as a client.
   *
   * @returns true if running in client mode
   */
  public isClient(): boolean {
    return this.getType() === NetRuntimeType.Client;
  }

  /**
   * Check if running in headless mode (no rendering/UI).
   * Returns true for DedicatedServer mode by default.
   * Individual systems can override this by checking World.options.headless.
   *
   * @returns true if running in headless mode
   */
  public isHeadless(): boolean {
    return this.isDedicatedServer();
  }

  /**
   * Change the runtime type (for games that transition between modes).
   * This is primarily used when a game transitions from Standalone to Client mode.
   * @internal
   */
  public setType(type: NetRuntimeType): void {
    if (this.runtimeType !== type) {
      console.log(`[NetRuntime] Changing runtime type from ${this.runtimeType} to ${type}`);
      this.runtimeType = type;
    }
  }

  /**
   * Reset the runtime state (primarily for testing).
   * @internal
   */
  public reset(): void {
    this.runtimeType = null;
    this.initialized = false;
  }
}

// Export singleton instance
export const NetRuntime = new NetRuntimeManager();

